import React, { useState, useRef, useEffect } from 'react';
import { Play, RefreshCw, Volume2, VolumeX } from 'lucide-react';
import { cn } from '../../utils/cn';
import { eggReadinessOptions } from '../../data/eggData';

interface TimerDisplayProps {
  seconds: number;
  initialSeconds: number;
  isActive: boolean;
  isCompleted: boolean;
  onStart: () => void;
  onReset: () => void;
  readiness: typeof eggReadinessOptions[0];
}

const TimerDisplay = ({
  seconds,
  initialSeconds,
  isActive,
  isCompleted,
  onStart,
  onReset,
  readiness,
}: TimerDisplayProps) => {
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const audioContextRef = useRef<AudioContext | null>(null);
  const tickSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const completeSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const tickBufferRef = useRef<AudioBuffer | null>(null);
  const completeBufferRef = useRef<AudioBuffer | null>(null);
  const isIOSRef = useRef(false);

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = initialSeconds > 0 
    ? ((initialSeconds - seconds) / initialSeconds) * 100 
    : 0;

  const fillPercentage = isCompleted ? 100 : progressPercentage;

  // Initialize audio context and check for iOS
  useEffect(() => {
    isIOSRef.current = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    
    const initAudio = async () => {
      try {
        // Create AudioContext with iOS optimizations
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
          latencyHint: isIOSRef.current ? 'interactive' : 'playback',
          sampleRate: isIOSRef.current ? 44100 : undefined
        });

        // Load tick sound
        const tickResponse = await fetch('https://assets.mixkit.co/active_storage/sfx/1060/1060-preview.mp3');
        const tickArrayBuffer = await tickResponse.arrayBuffer();
        tickBufferRef.current = await audioContextRef.current.decodeAudioData(tickArrayBuffer);

        // Load complete sound
        const completeResponse = await fetch('https://assets.mixkit.co/active_storage/sfx/2575/2575-preview.mp3');
        const completeArrayBuffer = await completeResponse.arrayBuffer();
        completeBufferRef.current = await audioContextRef.current.decodeAudioData(completeArrayBuffer);

        // Unlock audio on iOS
        if (isIOSRef.current) {
          const unlockAudio = async () => {
            if (audioContextRef.current?.state === 'suspended') {
              await audioContextRef.current.resume();
              const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
              const source = audioContextRef.current.createBufferSource();
              source.buffer = buffer;
              source.connect(audioContextRef.current.destination);
              source.start(0);
              document.removeEventListener('touchend', unlockAudio);
            }
          };
          document.addEventListener('touchend', unlockAudio);
        }
      } catch (error) {
        console.error('Error initializing audio:', error);
      }
    };

    initAudio();

    // Handle visibility change
    const handleVisibilityChange = async () => {
      if (!document.hidden && audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopAllSounds();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const stopAllSounds = () => {
    if (tickSourceRef.current) {
      tickSourceRef.current.stop();
      tickSourceRef.current.disconnect();
      tickSourceRef.current = null;
    }
    if (completeSourceRef.current) {
      completeSourceRef.current.stop();
      completeSourceRef.current.disconnect();
      completeSourceRef.current = null;
    }
  };

  const playTickSound = async () => {
    if (!audioContextRef.current || !tickBufferRef.current || !isSoundEnabled) return;

    try {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      stopAllSounds();

      tickSourceRef.current = audioContextRef.current.createBufferSource();
      tickSourceRef.current.buffer = tickBufferRef.current;
      tickSourceRef.current.loop = true;

      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = 0.5;
      
      tickSourceRef.current.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      tickSourceRef.current.start();
    } catch (error) {
      console.error('Error playing tick sound:', error);
    }
  };

  const playCompleteSound = async () => {
    if (!audioContextRef.current || !completeBufferRef.current || !isSoundEnabled) return;

    try {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      stopAllSounds();

      completeSourceRef.current = audioContextRef.current.createBufferSource();
      completeSourceRef.current.buffer = completeBufferRef.current;
      completeSourceRef.current.loop = true;

      const gainNode = audioContextRef.current.createGain();
      gainNode.gain.value = 1;

      completeSourceRef.current.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      completeSourceRef.current.start();
    } catch (error) {
      console.error('Error playing complete sound:', error);
    }
  };

  // Handle timer state changes
  useEffect(() => {
    if (isActive && isSoundEnabled) {
      playTickSound();
    } else if (!isActive) {
      stopAllSounds();
    }

    if (isCompleted && isSoundEnabled) {
      stopAllSounds();
      playCompleteSound();
    }
  }, [isActive, isCompleted, isSoundEnabled]);

  const toggleSound = () => {
    setIsSoundEnabled(!isSoundEnabled);
    if (isSoundEnabled) {
      stopAllSounds();
    } else if (isActive) {
      playTickSound();
    } else if (isCompleted) {
      playCompleteSound();
    }
  };

  const handleStart = async () => {
    stopAllSounds();
    if (audioContextRef.current?.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    onStart();
  };

  const handleReset = () => {
    stopAllSounds();
    onReset();
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-64 h-80 mb-6">
        <div className="absolute inset-0 bg-white rounded-t-[50%] rounded-b-[35%] shadow-[0_0_30px_rgba(255,215,0,0.3)]"></div>
        
        <div className="absolute top-[60%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40">
          <div className={cn(
            "relative w-full h-full rounded-full overflow-hidden",
            "bg-[#FFD900]/20 backdrop-blur-none",
            isCompleted ? "bg-[#FFD900]/10" : "bg-[#FFD900]/20"
          )}>
            <div 
              className={cn(
                "absolute bottom-0 left-0 right-0 bg-[#FFD900]",
                "transition-all duration-100 ease-linear",
                isCompleted ? "!bg-[#FFD900]" : "bg-[#FFD900]"
              )}
              style={{ 
                height: `${fillPercentage}%`,
                backgroundColor: '#FFD900',
                WebkitBackfaceVisibility: 'hidden',
                WebkitTransform: 'translate3d(0,0,0)'
              }}
            />
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center -translate-y-[28%]">
          <div className="text-center -rotate-[7deg]">
            {!isActive && seconds === 0 && !isCompleted ? (
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-600">Select options</p>
                <p className="text-3xl font-bold text-gray-800">
                  Ready?
                </p>
              </div>
            ) : (
              <>
                <p className="text-lg font-medium text-gray-600">{readiness.name}</p>
                <p className="text-4xl font-bold text-gray-800">
                  {formatTime(seconds)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        {!isActive ? (
          <>
            <button 
              onClick={handleStart}
              className={cn(
                "flex items-center px-6 py-3 bg-primary-500 text-white rounded-full shadow-md hover:bg-primary-600 transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50",
                seconds > 0 && "bg-green-500 hover:bg-green-600"
              )}
              disabled={isActive}
            >
              <Play className="w-5 h-5 mr-2" />
              {seconds > 0 ? "Resume" : "Start Timer"}
            </button>
            {isCompleted && (
              <button 
                onClick={handleReset}
                className="flex items-center px-6 py-3 bg-gray-200 text-gray-700 rounded-full shadow-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Reset
              </button>
            )}
          </>
        ) : (
          <>
            <button 
              onClick={handleReset}
              className="flex items-center px-6 py-3 bg-gray-200 text-gray-700 rounded-full shadow-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Reset
            </button>
            <button
              onClick={toggleSound}
              className={cn(
                "flex items-center px-4 py-3 rounded-full shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50",
                isSoundEnabled 
                  ? "bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500" 
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-300"
              )}
              aria-label={isSoundEnabled ? "Disable sound" : "Enable sound"}
            >
              {isSoundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TimerDisplay;